/**
 * Cart Utils
 * ----------------------------------
 * Shared methods that are used in and around the cart, that do not directly interact with the Ajax cart API.
 * Note, see cart-api.js for methods that interact with the cart API.
 *
 * First methods are direct wrappers on cart-api
 * Following ones are other useful methods
 */

import {
  addItemsToCartRequest,
  changeItemQuantityRequest,
  fetchCartRequest,
  removeVariantsRequest
} from '@/js/cart-api';
import { updateElementState } from '@/js/element-states';
import DynamicSection from '../../modules/dynamic-section/dynamic-section';

// Wrapper methods on cart-api

// TODO - rename / update comments for this function. Its expecting a form, not an array of objects
// TODO - move the setFlippedState() to the product card js, as it's not a cart utility
/**
 * Adds a variants to the cart
 *
 * @param {Array.<object>} items - An array of items to add to the cart
 * @param {string} items.id - The ID of the variant to add to the cart
 * @param {number} items.quantity - The quantity of the variant to add to the cart
 * @param {HTMLElement} button - The button that was clicked to add the product to the cart
 * @param {Array.<String>} additionalSections - Additional sections to update when the cart is updated
 *
 * @returns {void}
 */
async function addItemsToCart(form, button, additionalSections = []) {
  const sectionsRequired = additionalSections;

  try {
    updateElementState(button, 'loading');

    let productData = Object.fromEntries(form.entries());
    const response = await addItemsToCartRequest(
      [productData],
      sectionsRequired
    );
    const responseJson = await response.json();

    // TODO when adding to cart, we need to section re-render the button (or do we?), in case the product is now out of stock. How do we ensure we still see the button tooltip and loading state?
    if (response.ok) {
      updateElementState(button, 'success');
      emitAddToCartSuccessEvent();
      onCartUpdateSuccess(responseJson);
    } else {
      updateElementState(button);
      emitNotificationEvent(responseJson.description);
      // update all the sections passed in the additionalSections array
      for (const sectionId of sectionsRequired) {
        const section = document.querySelector(
          `[data-section-id="${sectionId}"]`
        );
        section.reRenderSection();
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error adding product to cart: ${JSON.stringify(error)}`);
  }
}

/**
 * Update the quantity of the line item
 * @param {string} id - The id of the line item to update
 * @param {number} quantity - The quantity to update the line item to
 * @param {HTMLElement} element - The element where the loading state is updated
 * @param {Array.<String>} additionalSections - Additional sections to update when the cart is updated
 * @returns {void}
 */

async function updateLineQuantity(
  id,
  quantity,
  element,
  additionalSections = []
) {
  updateElementState(element, 'loading');
  const cart = document.querySelector('[data-cart-main]');
  const sectionsRequired = additionalSections;

  try {
    const response = await changeItemQuantityRequest(
      id,
      quantity,
      sectionsRequired
    );

    let scrollPosition = document.querySelector('[data-items]')?.scrollTop;

    const responseJson = await response.json();

    if (response.ok) {
      onCartUpdateSuccess(responseJson);
      document.querySelector('[data-items]')?.scrollTo(0, scrollPosition);
      updateElementState(cart, 'success', 'Cart updated successfully');
    } else {
      await cart.reRenderSection();
      emitNotificationEvent(responseJson.description);
      updateElementState(cart);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error with PUT request', error);
  }
}

/**
 * Removes line items from the cart
 *
 * @param {Array.<Number>} itemIds - An array of either variant IDs or line item keys to remove from the cart
 * @param {HTMLElement} button - The button that was clicked to remove the product from the cart
 * @param {Array.<String>} additionalSections - Additional sections to update when the cart is updated
 *
 * @returns {void}
 */
async function removeLineItems(itemIds, button, additionalSections = []) {
  const sectionsRequired = additionalSections;
  updateElementState(button, 'loading');

  try {
    const response = await removeVariantsRequest(itemIds, sectionsRequired);
    const responseJson = await response.json();

    if (response.ok) {
      updateElementState(button, 'success');
      onCartUpdateSuccess(responseJson);
    } else {
      updateElementState(button, 'error', responseJson.description);
      // update all the sections passed in the additionalSections array
      for (const sectionId of sectionsRequired) {
        const section = document.querySelector(
          `[data-section-id="${sectionId}"]`
        );
        section.reRenderSection();
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Error removing product from cart: ${JSON.stringify(error)}`);
  }
}

// Other useful methods that don't directly interact with the cart-api.js methods

/**
 * Updates the value and count on the cart icon.
 * Uses passed in value, or else pulls from the cart.js endpoint
 *
 * @param {Number} passedItemCount (optional) - The number of items in the cart
 * @returns {void}
 */
const updateCartIcon = async passedItemCount => {
  const cartIcon = document.querySelector('cart-icon');
  if (!cartIcon) return;
  let itemCount;

  if (passedItemCount) {
    itemCount = passedItemCount;
  } else {
    const response = await fetchCartRequest();
    const json = await response.json();
    itemCount = json.item_count;
  }
  cartIcon.setCount(itemCount);
};

/**
 * Emit an event when a product is added to the cart
 * @returns {void}
 */
async function emitAddToCartSuccessEvent() {
  const event = new CustomEvent('pxl:addToCartSuccess', {
    bubbles: true
  });

  document.dispatchEvent(event);
}

/**
 * Adds a listener to element that will dispatch a custom event to toggle the flyout cart
 * @param {HtmlElement} element - The element to listen for clicks on
 * @returns {void}
 */
const setFlyoutCartToggleEventListener = element => {
  element.addEventListener('click', event => {
    event.preventDefault();

    element.dispatchEvent(
      new CustomEvent('pxl:flyoutCart:toggle', {
        bubbles: true
      })
    );
  });
};

/**
 * Handle cart API response and update sections with dynamic content
 * @param {JSON} cartResponseJson - json() from the response from cart API
 */
const onCartUpdateSuccess = async ({ sections, item_count }) => {
  updateCartIcon(item_count);
  DynamicSection.replaceMultipleSectionsContent(sections, {
    // the <flyout-cart> is a popover. We want to maintain its state, so we only update the children
    morphdomOptions: { childrenOnly: true }
  });
};

const emitNotificationEvent = notification => {
  document.dispatchEvent(
    new CustomEvent('pxl:notification', {
      detail: { notification },
      bubbles: true
    })
  );
};

export {
  addItemsToCart,
  onCartUpdateSuccess,
  setFlyoutCartToggleEventListener,
  updateCartIcon,
  updateLineQuantity,
  removeLineItems,
  emitNotificationEvent
};
